<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { api } from "@/api/client";
import { describeError } from "@/utils/errors";
import type { Organization } from "@/api/types";
import { useAuthStore } from "@/stores/auth";

const { t } = useI18n();
const auth = useAuthStore();

const items = ref<Organization[]>([]);
const loading = ref(true);
const listError = ref<string | null>(null);
const search = ref("");

const formError = ref<string | null>(null);
const submitting = ref(false);
const newSlug = ref("");
const newName = ref("");

const isOwner = computed(() => auth.hasRole(["owner"]));

async function reload() {
  loading.value = true;
  listError.value = null;
  try {
    const resp = await api.admin.organizations.list();
    items.value = resp.organizations;
  } catch (err) {
    listError.value = err instanceof Error ? err.message : "load failed";
  } finally {
    loading.value = false;
  }
}

async function submit() {
  formError.value = null;
  submitting.value = true;
  try {
    await api.admin.organizations.create({
      slug: newSlug.value.trim(),
      name: newName.value.trim(),
    });
    newSlug.value = "";
    newName.value = "";
    await reload();
  } catch (err) {
    formError.value = errorMessage(err);
  } finally {
    submitting.value = false;
  }
}

async function remove(id: string) {
  if (!window.confirm(t("admin.confirm.delete_org"))) return;
  try {
    await api.admin.organizations.delete(id);
    await reload();
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

function errorMessage(err: unknown): string {
  return describeError(err, t);
}

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return items.value;
  return items.value.filter(
    (o) => o.slug.toLowerCase().includes(q) || o.name.toLowerCase().includes(q),
  );
});

onMounted(reload);
</script>

<template>
  <div class="admin-page">
    <div class="admin-page__header">
      <h1>{{ t("admin.nav.organizations") }}</h1>
      <input v-model="search" type="search" :placeholder="t('admin.search')" />
    </div>

    <section v-if="isOwner" class="admin-card">
      <h2 style="margin-top: 0; font-size: 1rem">{{ t("admin.organizations.create_title") }}</h2>
      <form class="admin-form" @submit.prevent="submit">
        <label>
          <span>{{ t("admin.organizations.slug") }}</span>
          <input v-model="newSlug" required :disabled="submitting" />
        </label>
        <label>
          <span>{{ t("admin.organizations.name") }}</span>
          <input v-model="newName" required :disabled="submitting" />
        </label>
        <button type="submit" class="admin-form__submit" :disabled="submitting">
          {{ submitting ? t("admin.submitting") : t("admin.create") }}
        </button>
      </form>
      <p v-if="formError" class="admin-error" style="margin-top: 0.5rem">{{ formError }}</p>
    </section>

    <section class="admin-card">
      <p v-if="loading" class="admin-empty">{{ t("admin.loading") }}</p>
      <p v-else-if="listError" class="admin-error">{{ listError }}</p>
      <p v-else-if="filtered.length === 0" class="admin-empty">{{ t("admin.no_results") }}</p>
      <table v-else class="admin-table">
        <thead>
          <tr>
            <th>{{ t("admin.organizations.slug") }}</th>
            <th>{{ t("admin.organizations.name") }}</th>
            <th>{{ t("admin.id") }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="org in filtered" :key="org.id">
            <td :data-label="t('admin.organizations.slug')">{{ org.slug }}</td>
            <td :data-label="t('admin.organizations.name')">{{ org.name }}</td>
            <td class="admin-table__id" :data-label="t('admin.id')">{{ org.id.slice(0, 8) }}</td>
            <td class="admin-table__actions">
              <button v-if="isOwner" type="button" @click="remove(org.id)">
                {{ t("admin.delete") }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>
